import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Location } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppStateService } from '../../services/app-state.service';
import { WallpaperService } from '../../services/wallpaper.service';
import { JourneyContext, INITIAL_CONTEXT, MasterItem, MastersResponse, SearchWallpaper, buildPdpUrl } from '../../models/wallpaper.model';
import {
  CATEGORIES, CATEGORY_DISPLAY_MAP, SPACES, SPACE_DISPLAY_MAP,
  categoryIdFromToken, spaceIdFromToken, categorySlugById, spaceSlugById, toUrlSlug
} from '../../models/constants';
import { DataLayerService } from '../../services/datalayer.service';
import { TriggerService } from '../../services/trigger.service';

@Component({
  selector: 'app-listing',
  templateUrl: './listing.component.html'
})
export class ListingComponent implements OnInit, OnDestroy {
  // Wallpaper data
  wallpapers: SearchWallpaper[] = [];
  isLoading = false;
  loadError = false;

  // Masters (sidebar filter options)
  masters: MastersResponse | null = null;
  mastersLoading = false;

  // Dedicated lists loaded from separate endpoints (more reliable than masters for categories/spaces)
  categoriesList: MasterItem[] = [];
  spacesList: MasterItem[] = [];

  // Facet counts from search API (keyed by space/category name, used for count display)
  spaceFacets: Record<string, number> = {};
  categoryFacets: Record<string, number> = {};

  // Pagination
  currentPage = 1;
  readonly pageSize = 12;
  totalRecords = 0;

  // Context & user state
  context: JourneyContext = { ...INITIAL_CONTEXT };
  wishlistIds = new Set<string>();

  // Sidebar UI state
  sortBy = 'Newest';
  showFiltersMobile = false;
  expandedSections = new Set<string>(['m-category', 'm-space']);
  viewAllFilterType: 'space' | 'category' | null = null;
  circleRowType: 'space' | 'category' | null = null; // persists circle row even after clearing filters
  pendingSelectAll: 'space' | 'category' | null = null; // waiting for masters to load

  // Selected filters — IDs for category/space/theme/color/collection/subcat, names for type/quality
  selectedCategoryIds = new Set<number>();
  selectedSpaceIds = new Set<number>();
  selectedThemeIds = new Set<number>();
  selectedColorIds = new Set<number>();   // color IDs for UI; translated to names for API
  selectedCollectionIds = new Set<number>();   // collection IDs (UI tracking)
  selectedSubCatIds = new Set<number>();   // sub-category IDs (UI tracking)
  selectedTypes = new Set<string>();   // "concept" | "pattern"
  selectedQualities = new Set<string>();   // "standard" | "premium" | "exclusive"
  vastu = false;               // vastu-friendly filter

  sortOptions = ['Recommended', 'Popular', 'Newest', 'Price: Low → High', 'Price: High → Low'];
  showSortDropdown = false;

  private sub = new Subscription();
  private _pendingColorNames: string[] = [];

  // B8/B9/B12 tracking state
  private pageEnterTime = 0;
  private scrollMilestones = new Set<number>();
  private userActionCount = 0;
  private hesitationTimer: any;
  private exitIntentFired = false;

  constructor(
    public state: AppStateService,
    public router: Router,
    private route: ActivatedRoute,
    private wallpaperService: WallpaperService,
    private location: Location,
    private dataLayer: DataLayerService,
    private triggerService: TriggerService,
  ) { }

  goBack() {
    if (this.currentPage > 1) {
      this.goToPage(this.currentPage - 1);
    } else {
      this.router.navigate(['/wallpaper-home']);
    }
  }
  ngOnInit() {
    this.loadMasters();

    // React to URL query params: /wallpaper/category/modern?category=5&sortby=id&sortdir=desc
    this.sub.add(
      this.route.queryParamMap.subscribe(qp => {
        const categoryId = qp.get('category');
        const spaceId = qp.get('space');
        const themeParam = qp.get('theme');
        const colorParam = qp.get('color');
        const typeParam = qp.get('type');
        const qualityParam = qp.get('quality');
        const vastuParam = qp.get('vastu');
        const sortby = qp.get('sortby');
        const sortdir = qp.get('sortdir');
        const searchtext = qp.get('searchtext');
        const view = qp.get('view');
        const page = qp.get('page');
        if (page) this.currentPage = +page;

        this.selectedCategoryIds = new Set();
        this.selectedSpaceIds = new Set();
        this.selectedThemeIds = new Set();
        this.selectedColorIds = new Set();
        this.selectedTypes = new Set();
        this.selectedQualities = new Set();
        this.vastu = false;
        this.pendingSelectAll = null;

        if (themeParam) {
          this.selectedThemeIds = new Set(themeParam.split(',').map(Number).filter(Boolean));
        }
        if (typeParam) {
          this.selectedTypes = new Set(typeParam.split(',').map(s => s.trim()).filter(Boolean));
        }
        if (qualityParam) {
          this.selectedQualities = new Set(qualityParam.split(',').map(s => s.trim()).filter(Boolean));
        }
        if (vastuParam === 'yes' || vastuParam === 'true') {
          this.vastu = true;
        }
        // colorParam comes from header LLM as comma-separated NAMES; resolve to IDs
        // once masters load. Stash names so masters loader can map them.
        this._pendingColorNames = colorParam ? colorParam.split(',').map(s => s.trim()).filter(Boolean) : [];
        this.applyPendingColorNames();

        if (view === 'all-categories') {
          this.circleRowType = 'category';
          const catParam = qp.get('category');
          if (catParam) {
            // Specific categories selected — not "all", so clear viewAll chip
            this.selectedCategoryIds = this.parseCategoryParam(catParam);
            this.viewAllFilterType = null;
          } else {
            this.selectedCategoryIds = new Set();
            this.viewAllFilterType = 'category';
          }
        } else if (view === 'all-spaces') {
          this.circleRowType = 'space';
          const spaceParam = qp.get('space');
          if (spaceParam) {
            this.selectedSpaceIds = this.parseSpaceParam(spaceParam);
            this.viewAllFilterType = null;
          } else {
            this.selectedSpaceIds = new Set();
            this.viewAllFilterType = 'space';
          }
        } else if (categoryId) {
          this.selectedCategoryIds = this.parseCategoryParam(categoryId);
          this.selectedSpaceIds = new Set();
          this.viewAllFilterType = null;
          this.circleRowType = 'category';
        } else if (spaceId) {
          this.selectedSpaceIds = this.parseSpaceParam(spaceId);
          this.selectedCategoryIds = new Set();
          this.viewAllFilterType = null;
          this.circleRowType = 'space';
        } else {
          this.viewAllFilterType = null;
        }

        if (sortby === 'price' && sortdir === 'asc') this.sortBy = 'Price: Low → High';
        else if (sortby === 'price' && sortdir === 'desc') this.sortBy = 'Price: High → Low';
        else this.sortBy = 'Newest';

        if (searchtext) {
          this.state.updateContext({ search_query: decodeURIComponent(searchtext) });
        }


        this.sub.add(
          this.state.context$.subscribe(c => {
            this.context = c;

            console.log("UPDATED CONTEXT:", c.search_query);

            if (!this.pendingSelectAll) {
              this.loadWallpapers();
            }
          })
        );
      })
    );

    this.sub.add(this.state.context$.subscribe(c => this.context = c));
    this.sub.add(this.state.wishlistIds$.subscribe(ids => this.wishlistIds = ids));

    // B8/B9/B12 init
    this.pageEnterTime = Date.now();
    this.scrollMilestones = new Set();
    this.userActionCount = 0;
    this.exitIntentFired = false;
    this.startHesitationTimer();
    // T7 — fire if 90s on listing without product click
    this.triggerService.listingStartWatch();
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
    clearTimeout(this.hesitationTimer);
    this.triggerService.listingStopWatch();
  }

  // B8 — scroll depth tracking
  @HostListener('window:scroll')
  onWindowScroll(): void {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (docHeight <= 0) return;
    const pct = Math.round((scrollTop / docHeight) * 100);
    [25, 50, 75, 100].forEach(milestone => {
      if (pct >= milestone && !this.scrollMilestones.has(milestone)) {
        this.scrollMilestones.add(milestone);
        this.dataLayer.pushPlpScroll({
          scrollPercent: milestone,
          timeSpent: Math.round((Date.now() - this.pageEnterTime) / 1000),
          filtersActive: this.buildActiveFilters()
        });
      }
    });
    this.userActionCount++;
  }

  // B9 — hesitation timer (fires at 90s if actions < 3)
  private startHesitationTimer(): void {
    clearTimeout(this.hesitationTimer);
    this.hesitationTimer = setTimeout(() => {
      if (this.userActionCount < 3) {
        this.dataLayer.pushUserHesitation({
          timeSpent: Math.round((Date.now() - this.pageEnterTime) / 1000),
          actionsTaken: this.userActionCount,
          filtersActive: this.buildActiveFilters()
        });
      }
    }, 90000);
  }

  // B12 — exit intent (mouse leaves viewport top)
  @HostListener('document:mouseleave', ['$event'])
  onMouseLeave(e: MouseEvent): void {
    if (e.clientY <= 0 && !this.exitIntentFired) {
      this.exitIntentFired = true;
      this.dataLayer.pushExitIntent({
        timeSpent: Math.round((Date.now() - this.pageEnterTime) / 1000),
        filtersActive: this.buildActiveFilters(),
        itemsViewed: this.wallpapers.length
      });
    }
  }

  // ── Masters ──────────────────────────────────────────────────
  loadMasters() {
    this.mastersLoading = true;

    // Load categories from dedicated endpoint
    this.wallpaperService.getCategories().subscribe({
      next: (res: any) => {
        const list: any[] = res?.data ?? res ?? [];
        const knownCatIds = new Set([...CATEGORIES.map(c => c.id), ...Object.keys(CATEGORY_DISPLAY_MAP).map(Number)]);
        this.categoriesList = list
          .filter(c => knownCatIds.has(c.id))
          .map(c => {
            const hardcoded = CATEGORY_DISPLAY_MAP[c.id] || CATEGORIES.find(cat => cat.id === c.id);
            return {
              id: c.id, name: c.name,
              display_name: hardcoded?.display_name || c.display_name || c.name,
              slug: c.slug, count: c.count,
              image: hardcoded?.image || ''
            };
          });
        if (this.pendingSelectAll === 'category') {
          this.selectedCategoryIds = new Set(this.categoriesList.map(c => c.id));
          this.pendingSelectAll = null;
          this.currentPage = 1;
          this.loadWallpapers();
        }
        this.resolveContextFilters();
      },
      error: () => {
        this.buildFiltersFromSearchApi();
      }
    });

    // Spaces are hardcoded — no API call needed
    this.spacesList = SPACES.map(s => ({
      id: s.id,
      name: s.display_name,
      display_name: s.display_name,
      slug: s.display_name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      image: s.image
    }));
    if (this.pendingSelectAll === 'space') {
      this.selectedSpaceIds = new Set(this.spacesList.map(s => s.id));
      this.pendingSelectAll = null;
      this.currentPage = 1;
      this.loadWallpapers();
    }
    this.resolveContextFilters();

    // Load masters for color/theme/collection data
    this.wallpaperService.getMasters().subscribe({
      next: (res: any) => {
        this.masters = res?.data ?? res;
        this.mastersLoading = false;
        this.applyPendingColorNames();
      },
      error: () => {
        this.mastersLoading = false;
        this.buildFiltersFromSearchApi();
      }
    });
  }

  private applyPendingColorNames() {
    if (!this._pendingColorNames.length) return;
    const palette = this.masterColors;
    if (!palette.length) return;
    const ids = this._pendingColorNames
      .map(name => palette.find(c => (c.name || '').toLowerCase() === name.toLowerCase())?.id)
      .filter((id): id is number => typeof id === 'number');
    if (ids.length) {
      this.selectedColorIds = new Set(ids);
    }
    this._pendingColorNames = [];
  }

  // Resolve context-based space/category names to IDs (for navigation from home page)
  private contextResolved = false;
  private resolveContextFilters() {
    if (this.contextResolved) return;
    // Only resolve if no query-param-based filters are already set
    if (this.selectedSpaceIds.size > 0 || this.selectedCategoryIds.size > 0) return;

    const spaceName = this.context.selected_space;
    if (spaceName && this.spacesList.length > 0) {
      const match = this.spacesList.find(s =>
        (s.display_name || s.name || '').toLowerCase() === spaceName.toLowerCase() ||
        (s.name || '').toLowerCase() === spaceName.toLowerCase()
      );
      if (match) {
        this.contextResolved = true;
        this.selectedSpaceIds = new Set([match.id]);
        this.currentPage = 1;
        this.loadWallpapers();
        return;
      }
    }

    const catName = this.context.selected_category;
    if (catName && this.categoriesList.length > 0) {
      const match = this.categoriesList.find(c =>
        (c.display_name || c.name || '').toLowerCase() === catName.toLowerCase() ||
        (c.name || '').toLowerCase() === catName.toLowerCase()
      );
      if (match) {
        this.contextResolved = true;
        this.selectedCategoryIds = new Set([match.id]);
        this.currentPage = 1;
        this.loadWallpapers();
      }
    }
  }

  spacesFromFacets = false;
  categoriesFromFacets = false;
  private filtersFromSearchBuilt = false;
  private buildFiltersFromSearchApi() {
    if (this.filtersFromSearchBuilt) return;
    if (this.spacesList.length > 0 && this.categoriesList.length > 0) return;
    this.filtersFromSearchBuilt = true;

    // Make an unfiltered search to get facet data
    this.wallpaperService.searchWallpapers({ images: 1, page: 1 }).subscribe({
      next: (res) => {
        const facets = res.facet_field;
        if (!facets) return;

        // Build spaces from facets if not loaded from backend
        // Facet keys can be IDs ("8") or names ("living room")
        if (this.spacesList.length === 0 && facets.spaces) {
          const built = this.buildListFromFacet(facets.spaces, SPACE_DISPLAY_MAP);
          this.spacesList = built.items;
          // Numeric facet keys ARE real space ids, so the API can keep receiving
          // ids; only synthetic name-keyed lists need the name-based query.
          this.spacesFromFacets = built.keyedByName;
        }

        // Build categories from facets if not loaded
        if (this.categoriesList.length === 0 && facets.themes) {
          const built = this.buildListFromFacet(facets.themes, CATEGORY_DISPLAY_MAP);
          this.categoriesList = built.items;
          this.categoriesFromFacets = built.keyedByName;
        }

        this.mastersLoading = false;
      },
      error: () => { this.mastersLoading = false; }
    });
  }

  /**
   * Turns a Solr facet block into filter options.
   *
   * The facet is keyed either by id ("8": 515) or by name ("living room": 515).
   * Id keys carry no label, so the display map supplies the human name — without
   * this the sidebar and circle row render raw ids. Ids missing from the map are
   * dropped, matching how the masters API path filters to known ids.
   */
  private buildListFromFacet(
    facet: Record<string, any>,
    displayMap: Record<number, { display_name: string; image: string }>
  ): { items: MasterItem[]; keyedByName: boolean } {
    const entries = Object.entries(facet);
    const keyedByName = entries.some(([key]) => !/^\d+$/.test(key));

    if (!keyedByName) {
      const items: MasterItem[] = [];
      const skipped: string[] = [];
      entries.forEach(([key, count]) => {
        const known = displayMap[+key];
        if (!known) {
          // No label for this id, so it is hidden rather than shown as a bare
          // number — same as the masters API path, which also keeps only known
          // ids. Reported so a newly added id is not lost without a trace.
          skipped.push(key);
          return;
        }
        items.push({
          id: +key,
          name: known.display_name,
          display_name: known.display_name,
          slug: toUrlSlug(known.display_name),
          count: count as number,
          image: known.image || '',
        });
      });
      if (skipped.length) {
        console.warn(`[listing] facet ids with no entry in the display map are hidden from the filters: ${skipped.join(', ')}`);
      }
      return { items, keyedByName: false };
    }

    let autoId = 9000;
    const items = entries.map(([key, count]) => {
      const isNumericKey = /^\d+$/.test(key);
      const known = isNumericKey ? displayMap[+key] : undefined;
      const label = known?.display_name || key.replace(/\b\w/g, c => c.toUpperCase());
      return {
        id: isNumericKey ? +key : ++autoId,
        name: key,
        display_name: label,
        slug: toUrlSlug(label),
        count: count as number,
        image: known?.image || '',
      };
    });
    return { items, keyedByName: true };
  }

  // Use dedicated lists for category/space; fall back to masters if dedicated lists are empty
  private _cachedSpaces: MasterItem[] = [];
  private _spaceCacheKey = '';

  get masterCategories(): MasterItem[] { return this.categoriesList.length > 0 ? this.categoriesList : (this.masters?.category || []); }
  get masterSpaces(): MasterItem[] {
    const list = this.spacesList.length > 0 ? this.spacesList : (this.masters?.space || []);
    const cacheKey = list.length + ':' + Object.keys(this.spaceFacets).length;
    if (cacheKey === this._spaceCacheKey) return this._cachedSpaces;
    this._spaceCacheKey = cacheKey;

    let result: MasterItem[];
    if (Object.keys(this.spaceFacets).length > 0) {
      result = list.map(s => {
        if (s.count) return s;
        const cnt = this.spaceFacets[String(s.id)]
          ?? this.spaceFacets[(s.display_name || s.name).toLowerCase()]
          ?? this.spaceFacets[(s.name).toLowerCase()];
        return { ...s, count: cnt || 0 };
      }).filter(s => s.count > 0); // Hide spaces with 0 wallpapers
    } else {
      result = list;
    }
    // Retail removed from SPACES constant — no runtime filter needed
    // Deduplicate by display_name (e.g. merge "Office" id 77 + id 79 into one circle)
    const seen = new Map<string, MasterItem>();
    for (const s of result) {
      const key = (s.display_name || s.name || '').toLowerCase();
      if (!seen.has(key)) {
        seen.set(key, s);
      } else {
        // Merge counts
        const existing = seen.get(key)!;
        if (s.count) existing.count = (existing.count || 0) + (s.count || 0);
      }
    }
    this._cachedSpaces = Array.from(seen.values());
    return this._cachedSpaces;
  }

  // Get all space IDs that share the same display_name as the given space
  private getRelatedSpaceIds(spaceId: number): number[] {
    const space = this.spacesList.find(s => s.id === spaceId);
    if (!space) return [spaceId];
    const displayName = (space.display_name || space.name || '').toLowerCase();
    return this.spacesList
      .filter(s => (s.display_name || s.name || '').toLowerCase() === displayName)
      .map(s => s.id);
  }
  private readonly fallbackColors: MasterItem[] = [
    { id: 1, name: 'Red', display_name: 'Red', slug: 'red', hex: '#DC2626' },
    { id: 2, name: 'Blue', display_name: 'Blue', slug: 'blue', hex: '#2563EB' },
    { id: 3, name: 'Green', display_name: 'Green', slug: 'green', hex: '#16A34A' },
    { id: 4, name: 'Yellow', display_name: 'Yellow', slug: 'yellow', hex: '#EAB308' },
    { id: 5, name: 'Orange', display_name: 'Orange', slug: 'orange', hex: '#EA580C' },
    { id: 7, name: 'Purple', display_name: 'Purple', slug: 'purple', hex: '#9333EA' },
    { id: 8, name: 'Brown', display_name: 'Brown', slug: 'brown', hex: '#92400E' },
    { id: 9, name: 'Black', display_name: 'Black', slug: 'black', hex: '#1F2937' },
    { id: 10, name: 'White', display_name: 'White', slug: 'white', hex: '#F9FAFB' },
    { id: 13, name: 'Teal', display_name: 'Teal', slug: 'teal', hex: '#0D9488' },
  ];
  get masterColors(): MasterItem[] {
    const apiColors = this.masters?.color || [];
    return apiColors.length > 0 ? apiColors : this.fallbackColors;
  }
  get masterThemes(): MasterItem[] { return this.masters?.['wallpaper-theme'] || []; }
  get masterCollections(): MasterItem[] { return this.masters?.['wallpaper-collection'] || []; }

  trackById(_index: number, item: MasterItem): number { return item.id; }

  // ── Wallpaper loading ─────────────────────────────────────────
  private loadStartTime = 0;

  loadWallpapers() {
    this.isLoading = true;
    this.loadError = false;
    this.loadStartTime = performance.now();
    // Keep the chip row in apply-order; every filter change reaches this method.
    this.syncFilterOrder();

    const { sortby, sortdir } = this.getSortParams();

    // Build comma-separated filter values as the search API expects
    let category: string | undefined;
    if (this.selectedCategoryIds.size > 0) {
      if (this.categoriesFromFacets) {
        category = [...this.selectedCategoryIds]
          .map(id => this.categoriesList.find(c => c.id === id)?.name)
          .filter(Boolean).join(',');
      } else {
        category = [...this.selectedCategoryIds].join(',');
      }
    } else if (this.context.selected_category_id) {
      category = String(this.context.selected_category_id);
    }

    let space: string | undefined;
    if (this.selectedSpaceIds.size > 0) {
      if (this.spacesFromFacets) {
        space = [...this.selectedSpaceIds]
          .map(id => this.spacesList.find(s => s.id === id)?.name)
          .filter(Boolean).join(',');
      } else {
        space = [...this.selectedSpaceIds].join(',');
      }
    } else if (this.context.selected_space_id) {
      space = String(this.context.selected_space_id);
    }

    const theme = this.selectedThemeIds.size > 0 ? [...this.selectedThemeIds].join(',') : undefined;
    // Translate selected color IDs → names for the search API
    const colorNames = [...this.selectedColorIds]
      .map(id => this.masterColors.find(c => c.id === id)?.name)
      .filter((n): n is string => !!n);
    const color = colorNames.length > 0 ? colorNames.join(',') : undefined;
    const type = this.selectedTypes.size > 0 ? [...this.selectedTypes].join(',') : undefined;
    const quality = this.selectedQualities.size > 0 ? [...this.selectedQualities].join(',') : undefined;

    console.log("this.context.search_query", this.context.search_query)
    this.wallpaperService.searchWallpapers({
      category,
      space,
      theme,
      color,
      type,
      quality,
      vastu: this.vastu ? 'yes' : undefined,
      searchtext: this.context.search_query || undefined,
      sortby,
      sortdir,
      page: this.currentPage,
      images: this.pageSize,
    }).subscribe({
      next: (res) => {
        this.wallpapers = this.applySpaceThumbnail(res.results || []);
        this.totalRecords = res.total ?? 0;
        this.isLoading = false;
        this.dataLayer.setPageTitle(this.pageTitle);
        this.pushViewItemList();
        // D1 + D2 — search events with actual results count from API
        if (this.context.search_query) {
          this.dataLayer.pushSearch({
            searchTerm: this.context.search_query,
            resultsCount: this.totalRecords,
            searchType: 'manual'
          });
          this.dataLayer.pushSearchResults({
            searchTerm: this.context.search_query,
            resultsCount: this.totalRecords,
            resultIds: this.wallpapers.map(w => w.wallpaper_id || String(w.id))
          });
        }
        // Cache facet counts on first unfiltered load for count display in sidebar
        if (res.facet_field) {
          if (!Object.keys(this.spaceFacets).length) this.spaceFacets = res.facet_field.spaces ?? {};
          if (!Object.keys(this.categoryFacets).length) this.categoryFacets = res.facet_field.themes ?? {};
        }
      },
      error: () => {
        this.loadError = true;
        this.isLoading = false;
      }
    });
  }

  // When a single space is active, show the matching space thumbnail
  private applySpaceThumbnail(wallpapers: SearchWallpaper[]): SearchWallpaper[] {
    // Get active space ID — from sidebar filter OR from journey context
    let spaceId: number | null = null;
    if (this.selectedSpaceIds.size === 1) {
      spaceId = [...this.selectedSpaceIds][0];
    } else if (this.selectedSpaceIds.size === 0 && this.context.selected_space_id) {
      spaceId = +this.context.selected_space_id;
    }
    if (!spaceId) return wallpapers;

    return wallpapers.map(wp => {
      // Match exact space ID — only swap if this wallpaper actually has a mockup for this space
      if (wp.thumbnail_space1_id == spaceId && wp.space1) return { ...wp, list_thumbnail: wp.space1 };
      if (wp.thumbnail_space2_id == spaceId && wp.space2) return { ...wp, list_thumbnail: wp.space2 };
      if (wp.thumbnail_space3_id == spaceId && wp.space3) return { ...wp, list_thumbnail: wp.space3 };
      // No matching mockup for this space — keep original list_thumbnail
      return wp;
    });
  }

  private getSortParams(): { sortby: string; sortdir: string } {
    if (this.sortBy === 'Price: Low → High') return { sortby: 'price', sortdir: 'asc' };
    if (this.sortBy === 'Price: High → Low') return { sortby: 'price', sortdir: 'desc' };
    if (this.sortBy === 'Popular') return { sortby: 'updated_at', sortdir: 'desc' };
    if (this.sortBy === 'Recommended') return { sortby: 'recommended', sortdir: 'desc' };
    return { sortby: 'updated_at', sortdir: 'desc' };
  }

  get activeSpaceName(): string {
    if (this.selectedSpaceIds.size >= 1) {
      const id = [...this.selectedSpaceIds][0];
      const space = this.masterSpaces.find(s => s.id === id);
      return space?.display_name || space?.name || '';
    }
    return '';
  }

  get activeCategoryName(): string {
    if (this.selectedCategoryIds.size === 1) {
      const id = [...this.selectedCategoryIds][0];
      const cat = this.masterCategories.find(c => c.id === id);
      return cat?.display_name || cat?.name || '';
    }
    return '';
  }

  // Determines if circle row should show and which type
  get circleFilterType(): 'space' | 'category' | null {
    if (this.viewAllFilterType) return this.viewAllFilterType;
    if (this.selectedSpaceIds.size > 0) return 'space';
    if (this.selectedCategoryIds.size > 0) return 'category';
    // Keep circle row visible even after clearing "All" chip
    return this.circleRowType;
  }

 selectAllCircleItems() {
  const type = this.circleFilterType;

  if (type === 'space') {
    // Toggle All on/off
    if (this.isSpaceAllSelected) {
      this.viewAllFilterType = null;
      this.selectedSpaceIds = new Set();
    } else {
      this.viewAllFilterType = 'space';
      this.selectedSpaceIds = new Set();
    }
  } else if (type === 'category') {
    if (this.isCategoryAllSelected) {
      this.viewAllFilterType = null;
      this.selectedCategoryIds = new Set();
    } else {
      this.viewAllFilterType = 'category';
      this.selectedCategoryIds = new Set();
    }
  }

  this.currentPage = 1;
  this.replaceUrlFromState();
  this.loadWallpapers();
}

  get circleItems(): MasterItem[] {
    if (this.circleFilterType === 'space') return this.masterSpaces;
    if (this.circleFilterType === 'category') return this.masterCategories;
    return [];
  }

  // When "All" is active, show ALL circles as selected
  get circleSelectedIds(): Set<number> {
    if (this.circleFilterType === 'space') {
      if (this.isSpaceAllSelected) return new Set(this.masterSpaces.map(s => s.id));
      return this.selectedSpaceIds;
    }
    if (this.circleFilterType === 'category') {
      if (this.isCategoryAllSelected) return new Set(this.masterCategories.map(c => c.id));
      return this.selectedCategoryIds;
    }
    return new Set();
  }

  selectCircleItem(item: MasterItem) {
    if (this.circleFilterType === 'space') {
      if (this.isSpaceAllSelected) {
        // "All" was on → deselect this one item, select all others explicitly
        this.viewAllFilterType = null;
        this.selectedSpaceIds = new Set(this.masterSpaces.map(s => s.id).filter(id => id !== item.id));
      } else if (this.selectedSpaceIds.has(item.id)) {
        // Already selected → deselect it
        const next = new Set(this.selectedSpaceIds);
        next.delete(item.id);
        if (next.size === 0) {
          // Nothing left → go back to All
          this.viewAllFilterType = 'space';
          this.selectedSpaceIds = new Set();
        } else {
          this.selectedSpaceIds = next;
        }
      } else {
        // Not selected → add it
        const next = new Set([...this.selectedSpaceIds, item.id]);
        if (next.size >= this.masterSpaces.length) {
          // All selected individually → switch to All
          this.viewAllFilterType = 'space';
          this.selectedSpaceIds = new Set();
        } else {
          this.selectedSpaceIds = next;
        }
      }
      this.currentPage = 1;
      this.replaceUrlFromState();
      this.loadWallpapers();
    } else if (this.circleFilterType === 'category') {
      if (this.isCategoryAllSelected) {
        // "All" was on → user clicked one circle → single-select just that item
        this.viewAllFilterType = null;
        this.selectedCategoryIds = new Set([item.id]);
      } else if (this.selectedCategoryIds.has(item.id)) {
        // Already selected → deselect it
        const next = new Set(this.selectedCategoryIds);
        next.delete(item.id);
        if (next.size === 0) {
          // Nothing left → go back to All
          this.viewAllFilterType = 'category';
          this.selectedCategoryIds = new Set();
        } else {
          this.selectedCategoryIds = next;
        }
      } else {
        // Not selected → add it (multi-select)
        this.viewAllFilterType = null;
        this.selectedCategoryIds = new Set([...this.selectedCategoryIds, item.id]);
      }
      this.currentPage = 1;
      // Persist selection to URL so pagination restores it
      this.replaceUrlFromState();
      this.loadWallpapers();
    }
  }

  getCircleImage(item: MasterItem): string {
    if (this.circleFilterType === 'space') {
      return SPACE_DISPLAY_MAP[item.id]?.image || item.image || '';
    }
    return CATEGORY_DISPLAY_MAP[item.id]?.image || item.image || '';
  }

  // ── Pagination ────────────────────────────────────────────────
  get filteredWallpapers(): SearchWallpaper[] { return this.wallpapers; }
  get paginatedWallpapers(): SearchWallpaper[] { return this.wallpapers; }
  get totalPages(): number { return Math.ceil(this.totalRecords / this.pageSize); }
  get pageNumbers(): number[] {
    const total = this.totalPages;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages: number[] = [1];
    if (this.currentPage > 3) pages.push(-1);
    for (let i = Math.max(2, this.currentPage - 1); i <= Math.min(total - 1, this.currentPage + 1); i++) pages.push(i);
    if (this.currentPage < total - 2) pages.push(-1);
    pages.push(total);
    return pages;
  }

  goToPage(p: number) {
    if (p < 1 || p > this.totalPages || p === this.currentPage) return;
    const action = p === this.currentPage + 1 ? 'next' : p === this.currentPage - 1 ? 'prev' : 'page_number';
    this.currentPage = p;
    this.dataLayer.pushPaginationClick({
      action, pageNumber: p,
      totalResults: this.totalRecords,
      itemsLoaded: p * this.pageSize
    });
    this.userActionCount++;
    // Sync page + current filters to URL so queryParamMap restores them correctly.
    // Uses router.navigate (not replaceState) so each page gets a history entry.
    // Remove view=all-* from URL when specific filters are active (avoid "All Categories" chip appearing)
    const viewParam = this.viewAllFilterType === 'category' ? 'all-categories'
                    : this.viewAllFilterType === 'space' ? 'all-spaces'
                    : null;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        page: p > 1 ? p : null,
        category: this.categoryParamValue(),
        space: this.spaceParamValue(),
        view: viewParam
      },
      queryParamsHandling: 'merge',
      replaceUrl: false
    });
    this.loadWallpapers();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  buildActiveFilters(): string[] {
    const filters: string[] = [];
    this.selectedCategoryIds.forEach(id => {
      const c = this.categoriesList.find(x => x.id === id);
      filters.push('category:' + (c?.name || id));
    });
    this.selectedSpaceIds.forEach(id => {
      const s = this.spacesList.find(x => x.id === id);
      filters.push('space:' + (s?.name || id));
    });
    this.selectedColorIds.forEach(id => {
      const c = (this.masters?.color || []).find((x: any) => x.id === id);
      filters.push('color:' + (c?.name || id));
    });
    this.selectedThemeIds.forEach(id => filters.push('theme:' + id));
    this.selectedTypes.forEach(t => filters.push('type:' + t));
    this.selectedQualities.forEach(q => filters.push('quality:' + q));
    if (this.vastu) filters.push('vastu:yes');
    if (this.context.search_query) filters.push('search:' + this.context.search_query);
    return filters;
  }

  private get totalActiveFilterCount(): number {
    return this.selectedCategoryIds.size + this.selectedSpaceIds.size + this.selectedColorIds.size
      + this.selectedThemeIds.size + this.selectedTypes.size + this.selectedQualities.size
      + this.selectedCollectionIds.size + this.selectedSubCatIds.size + (this.vastu ? 1 : 0);
  }

  private pushViewItemList(): void {
    const filters = this.buildActiveFilters();
    this.dataLayer.pushViewItemList({
      listName: this.pageTitle,
      listId: this.context.selected_category ? 'category_' + this.context.selected_category.toLowerCase().replace(/\s+/g, '_')
        : this.context.selected_space ? 'space_' + this.context.selected_space.toLowerCase().replace(/\s+/g, '_')
        : 'all_wallpapers',
      items: this.wallpapers,
      filters,
      sortType: this.sortBy,
      totalResults: this.totalRecords,
      categoryName: this.context.selected_category || this.context.selected_space || this.pageTitle,
      category: this.context.selected_category || undefined,
      space: this.context.selected_space || undefined,
      timeToLoad: Math.round((performance.now() - this.loadStartTime) / 10) / 100,
    });
  }

  // ── Page title ────────────────────────────────────────────────
  get pageTitle(): string {
    if (this.context.search_query) return `Search: "${this.context.search_query}"`;
    const slug = this.route.snapshot.paramMap.get('slug');
    if (slug) return slug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    if (this.context.selected_space) return this.context.selected_space;
    if (this.context.selected_category) return this.context.selected_category;
    return 'All Wallpapers';
  }

  // ── Navigation ────────────────────────────────────────────────
  navigateToPDP(wp: SearchWallpaper, index?: number) {
    const sid = String(wp.id);
    this.state.updateContext({ interaction_history: [...this.context.interaction_history.filter(x => x !== sid), sid] });
    const idx = index ?? this.wallpapers.indexOf(wp) + 1;
    this.dataLayer.pushSelectItem({
      listName: this.pageTitle,
      listId: this.context.selected_category ? 'category_' + this.context.selected_category.toLowerCase().replace(/\s+/g, '_')
        : this.context.selected_space ? 'space_' + this.context.selected_space.toLowerCase().replace(/\s+/g, '_')
        : 'all_wallpapers',
      listType: this.context.selected_category ? 'category' : this.context.selected_space ? 'space' : 'trending',
      source: this.context.search_query ? 'search' : 'plp',
      item: wp,
      index: idx,
      categoryFallback: this.context.selected_category || this.context.selected_space || this.pageTitle,
    });
    this.triggerService.listingProductClicked();
    this.router.navigateByUrl(buildPdpUrl(wp));
  }

  // ── Wishlist ─────────────────────────────────────────────────
  toggleWishlistItem(wp: any, e: Event) {
    e.stopPropagation();
    const id = wp.id.toString();
    const isAdding = !this.wishlistIds.has(id);
    this.state.toggleWishlist(id, {
      id,
      name: wp.name,
      image: wp.list_thumbnail || wp.image,
      price: wp.price,
      wallpaper_type: wp.wallpaper_type,
      wallpaper_id: wp.wallpaper_id,
      collection_slug: wp.collection_slug,
      slug: wp.slug
    });
    if (isAdding) {
      this.dataLayer.pushAddToWishlist({
        item: wp, source: 'plp',
        categoryFallback: this.context.selected_category || this.context.selected_space || this.pageTitle
      });
    }
    this.userActionCount++;
  }

  similarSourceName = '';

  navigateToSimilar(wp: SearchWallpaper, e: Event) {
    e.stopPropagation();
    const width = 1100;
    const height = 700;
    const left = (screen.width - width) / 2;
    const top = (screen.height - height) / 2;
    // Extract meaningful keywords from the wallpaper name (remove noise words)
    const noise = new Set(['wallpaper', 'pattern', 'design', 'digital', 'print', 'the', 'and', 'with', 'for', 'of', 'in', 'on', 'a', 'an']);
    const keywords = (wp.name || '').split(/[\s\-_]+/)
      .filter(w => w.length > 2 && !noise.has(w.toLowerCase()))
      .slice(0, 5)
      .join(' ');
    const url = `/similar/${wp.slug || wp.id}?name=${encodeURIComponent(wp.name)}&keywords=${encodeURIComponent(keywords)}&collection=${wp.collection || ''}&category=${wp.collection || ''}`;
    window.open(url, '_blank', `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`);
  }

  clearSimilar() {
    this.similarSourceName = '';
    this.currentPage = 1;
    this.loadWallpapers();
  }

  isWishlisted(id: string): boolean { return this.wishlistIds.has(id); }

  // Solr can return `quality` either as a string ("exclusive") or a single-element array (["exclusive"]).
  private hasQuality(wp: any, quality: string): boolean {
    const q = wp?.quality;
    if (!q) return false;
    if (Array.isArray(q)) return q.some((v: any) => String(v).toLowerCase() === quality);
    return String(q).toLowerCase() === quality;
  }

  isExclusive(wp: any): boolean { return this.hasQuality(wp, 'exclusive'); }
  isPremium(wp: any): boolean { return this.hasQuality(wp, 'premium'); }

  // ── Sidebar filter helpers ────────────────────────────────────
  get activeFilterCount(): number {
    return this.selectedCategoryIds.size + this.selectedSpaceIds.size +
      this.selectedThemeIds.size + this.selectedColorIds.size +
      this.selectedCollectionIds.size + this.selectedSubCatIds.size +
      this.selectedTypes.size + this.selectedQualities.size +
      (this.vastu ? 1 : 0) +
      (this.viewAllFilterType ? 1 : 0);
  }

  get hasActiveFilters(): boolean {
    return this.activeFilterCount > 0;
  }

  /**
   * Chip keys in the order the filters were applied. Without this the chip row is
   * grouped by filter type, so adding a space after two categories makes it appear
   * ahead of them and the row looks like it reordered itself at random.
   */
  private filterOrder: string[] = [];

  private chipKey(chip: { type: string; id?: number; value?: string }): string {
    return chip.type + ':' + (chip.id ?? chip.value ?? '');
  }

  /**
   * Reconciles the remembered order with what is actually selected: drops keys for
   * filters that were removed, appends newly added ones at the end. Called from
   * loadWallpapers(), which every filter change already funnels through.
   */
  private syncFilterOrder(): void {
    const current = this.buildFilterChips().map(c => this.chipKey(c));
    const present = new Set(current);
    this.filterOrder = this.filterOrder.filter(k => present.has(k));
    current.forEach(k => { if (this.filterOrder.indexOf(k) === -1) this.filterOrder.push(k); });
  }

  /** First applied, first shown. Anything not yet in the order list trails behind. */
  get activeFilterChips(): { label: string; type: string; id?: number; value?: string }[] {
    const order = this.filterOrder;
    const rank = (chip: { type: string; id?: number; value?: string }) => {
      const i = order.indexOf(this.chipKey(chip));
      return i === -1 ? order.length : i;
    };
    return this.buildFilterChips().sort((a, b) => rank(a) - rank(b));
  }

  private buildFilterChips(): { label: string; type: string; id?: number; value?: string }[] {
    const chips: { label: string; type: string; id?: number; value?: string }[] = [];
    // Show "All Spaces"/"All Categories" chip when all are selected, plus individual chips
    if (this.isSpaceAllSelected) {
      chips.push({ label: 'All Spaces', type: 'all-spaces' });
    } else {
      this.selectedSpaceIds.forEach(id => {
        const s = this.masterSpaces.find(x => x.id === id);
        chips.push({ label: s?.display_name || s?.name || 'Space', type: 'space', id });
      });
    }
    if (this.isCategoryAllSelected) {
      chips.push({ label: 'All Categories', type: 'all-categories' });
    } else {
      this.selectedCategoryIds.forEach(id => {
        const c = this.masterCategories.find(x => x.id === id);
        chips.push({ label: c?.display_name || c?.name || 'Category', type: 'category', id });
      });
    }
    this.selectedTypes.forEach(t => chips.push({ label: t, type: 'type', value: t }));
    this.selectedQualities.forEach(q => chips.push({ label: q, type: 'quality', value: q }));
    this.selectedColorIds.forEach(id => {
      const c = this.masterColors.find(x => x.id === id);
      chips.push({ label: c?.display_name || c?.name || 'Color', type: 'color', id });
    });
    this.selectedThemeIds.forEach(id => {
      const t = this.masterThemes.find(x => x.id === id);
      chips.push({ label: t?.display_name || t?.name || 'Theme', type: 'theme', id });
    });
    this.selectedCollectionIds.forEach(id => {
      const c = this.masterCollections.find(x => x.id === id);
      chips.push({ label: c?.display_name || c?.name || 'Collection', type: 'collection', id });
    });
    this.selectedSubCatIds.forEach(id => {
      const s = this.masterSubCats.find(x => x.id === id);
      chips.push({ label: s?.display_name || s?.name || 'Sub-category', type: 'subcat', id });
    });
    if (this.vastu) chips.push({ label: 'Vastu Friendly', type: 'vastu' });
    if (this.context.search_query) chips.push({ label: `"${this.context.search_query}"`, type: 'search' });
    return chips;
  }

  removeChip(chip: { type: string; id?: number; value?: string }) {
    switch (chip.type) {
      case 'all-spaces':
        this.viewAllFilterType = null;
        this.selectedSpaceIds = new Set();
        this.state.updateContext({ selected_space: null, selected_space_id: null });
        break;
      case 'all-categories':
        this.viewAllFilterType = null;
        this.selectedCategoryIds = new Set();
        this.state.updateContext({ selected_category: null, selected_category_id: null });
        break;
      case 'space':
        this.selectedSpaceIds = this.toggleIdSet(this.selectedSpaceIds, chip.id!);
        if (this.selectedSpaceIds.size === 0) {
          this.state.updateContext({ selected_space: null, selected_space_id: null });
        }
        break;
      case 'category':
        this.selectedCategoryIds = this.toggleIdSet(this.selectedCategoryIds, chip.id!);
        if (this.selectedCategoryIds.size === 0) {
          this.state.updateContext({ selected_category: null, selected_category_id: null });
        }
        break;
      case 'color': this.selectedColorIds = this.toggleIdSet(this.selectedColorIds, chip.id!); break;
      case 'theme': this.selectedThemeIds = this.toggleIdSet(this.selectedThemeIds, chip.id!); break;
      case 'collection': this.selectedCollectionIds = this.toggleIdSet(this.selectedCollectionIds, chip.id!); break;
      case 'subcat': this.selectedSubCatIds = this.toggleIdSet(this.selectedSubCatIds, chip.id!); break;
      case 'type': this.selectedTypes = this.toggleStringSet(this.selectedTypes, chip.value!); break;
      case 'quality': this.selectedQualities = this.toggleStringSet(this.selectedQualities, chip.value!); break;
      case 'vastu': this.vastu = false; break;
      case 'search':
        this.state.updateContext({ search_query: null });
        break;
    }
    this.currentPage = 1;
    this.replaceUrlFromState();
    this.loadWallpapers();
  }

  // ── URL <-> filter-state mapping ──────────────────────────────
  // The URL carries readable slugs (?category=florals,art) while the search API
  // still wants numeric ids. These four helpers are the only place that
  // translates between the two, and they accept legacy id-based URLs so old
  // links and the header's LLM search (which sends ids) keep working.

  private parseCategoryParam(param: string): Set<number> {
    return this.parseIdOrSlugParam(param, categoryIdFromToken);
  }

  private parseSpaceParam(param: string): Set<number> {
    return this.parseIdOrSlugParam(param, spaceIdFromToken);
  }

  /** Splits a comma-separated param; per-token id/slug resolution lives in constants. */
  private parseIdOrSlugParam(param: string, resolve: (token: string) => number | null): Set<number> {
    const ids = (param || '').split(',')
      .map(token => resolve(token))
      .filter((id): id is number => id !== null);
    return new Set(ids);
  }

  /** Slugs for the URL; falls back to the raw id when a category isn't in the display map. */
  categoryParamValue(): string | null {
    if (this.selectedCategoryIds.size === 0) return null;
    return [...this.selectedCategoryIds].map(id => categorySlugById(id) || String(id)).join(',');
  }

  spaceParamValue(): string | null {
    if (this.selectedSpaceIds.size === 0) return null;
    return [...this.selectedSpaceIds].map(id => spaceSlugById(id) || String(id)).join(',');
  }

  private replaceUrlFromState() {
    const colorNames = [...this.selectedColorIds]
      .map(id => this.masterColors.find(c => c.id === id)?.name)
      .filter((n): n is string => !!n);
    const { sortby, sortdir } = this.getSortParams();
    const params: Record<string, string> = {};
    const categoryParam = this.categoryParamValue();
    const spaceParam = this.spaceParamValue();
    if (categoryParam) params['category'] = categoryParam;
    if (spaceParam) params['space'] = spaceParam;
    if (this.selectedThemeIds.size > 0) params['theme'] = [...this.selectedThemeIds].join(',');
    if (colorNames.length > 0) params['color'] = colorNames.join(',');
    if (this.selectedTypes.size > 0) params['type'] = [...this.selectedTypes].join(',');
    if (this.selectedQualities.size > 0) params['quality'] = [...this.selectedQualities].join(',');
    if (this.vastu) params['vastu'] = 'yes';
    if (this.context.search_query) params['searchtext'] = this.context.search_query;
    if (this.viewAllFilterType) {
      params['view'] = this.viewAllFilterType === 'space' ? 'all-spaces' : 'all-categories';
    }
    // Publish sort only for the price options: those are the ones the query-param
    // reader above restores. Emitting sortby=recommended / sortby=updated_at would
    // put a param in the URL that a reload silently ignores, so a shared link
    // would promise a sort it does not apply.
    if (sortby === 'price') { params['sortby'] = sortby; params['sortdir'] = sortdir; }
    if (this.currentPage > 1) params['page'] = String(this.currentPage);

    const tree = this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams: params,
    });
    this.location.replaceState(this.router.serializeUrl(tree));
  }

  clearAllFilters() {
    const cleared = this.buildActiveFilters();
    this.selectedCategoryIds = new Set();
    this.selectedSpaceIds = new Set();
    this.selectedThemeIds = new Set();
    this.selectedColorIds = new Set();
    this.selectedCollectionIds = new Set();
    this.selectedSubCatIds = new Set();
    this.selectedTypes = new Set();
    this.selectedQualities = new Set();
    this.vastu = false;
    this.viewAllFilterType = null;
    this.currentPage = 1;
    if (cleared.length > 0) {
      this.dataLayer.pushFilterClearAll({ filtersCleared: cleared, totalResultsAfter: this.totalRecords });
    }
    this.replaceUrlFromState();
    this.loadWallpapers();
  }

  toggleSection(s: string) {
    if (this.expandedSections.has(s)) this.expandedSections.delete(s);
    else this.expandedSections.add(s);
    this.expandedSections = new Set(this.expandedSections);
  }

  isSectionExpanded(s: string): boolean { return this.expandedSections.has(s); }

  private toggleIdSet(set: Set<number>, id: number): Set<number> {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  }

  private toggleStringSet(set: Set<string>, val: string): Set<string> {
    const next = new Set(set);
    if (next.has(val)) next.delete(val); else next.add(val);
    return next;
  }

  onCategoryToggle(id: number) {
    const was = this.selectedCategoryIds.has(id) || this.isCategoryAllSelected;
    const catName = this.categoriesList.find(c => c.id === id)?.name || String(id);
    if (this.isCategoryAllSelected) {
      this.viewAllFilterType = null;
      this.selectedCategoryIds = new Set(this.masterCategories.map(c => c.id).filter(cid => cid !== id));
    } else if (this.selectedCategoryIds.has(id)) {
      const next = new Set(this.selectedCategoryIds);
      next.delete(id);
      if (next.size === 0) {
        this.viewAllFilterType = 'category';
        this.selectedCategoryIds = new Set();
      } else {
        this.selectedCategoryIds = next;
      }
    } else {
      const next = new Set([...this.selectedCategoryIds, id]);
      if (next.size >= this.masterCategories.length) {
        this.viewAllFilterType = 'category';
        this.selectedCategoryIds = new Set();
      } else {
        this.viewAllFilterType = null;
        this.selectedCategoryIds = next;
      }
    }
    this.pushFilterEvent('category', catName, was);
    this.currentPage = 1;
    this.replaceUrlFromState();
    this.loadWallpapers();
  }
  onSpaceToggle(id: number) {
    const was = this.selectedSpaceIds.has(id) || this.isSpaceAllSelected;
    const spaceName = this.spacesList.find(s => s.id === id)?.name || String(id);
    if (this.isSpaceAllSelected) {
      this.viewAllFilterType = null;
      this.selectedSpaceIds = new Set(this.masterSpaces.map(s => s.id).filter(sid => sid !== id));
    } else if (this.selectedSpaceIds.has(id)) {
      const next = new Set(this.selectedSpaceIds);
      next.delete(id);
      if (next.size === 0) {
        this.viewAllFilterType = 'space';
        this.selectedSpaceIds = new Set();
      } else {
        this.selectedSpaceIds = next;
      }
    } else {
      const next = new Set([...this.selectedSpaceIds, id]);
      if (next.size >= this.masterSpaces.length) {
        this.viewAllFilterType = 'space';
        this.selectedSpaceIds = new Set();
      } else {
        this.viewAllFilterType = null;
        this.selectedSpaceIds = next;
      }
    }
    this.pushFilterEvent('space', spaceName, was);
    this.currentPage = 1;
    this.replaceUrlFromState();
    this.loadWallpapers();
  }
  onCategoryAllToggle() {
    if (this.isCategoryAllSelected) {
      this.selectedCategoryIds = new Set();
      this.viewAllFilterType = null;
    } else {
      this.selectedCategoryIds = new Set();
      this.viewAllFilterType = 'category';
    }
    this.currentPage = 1;
    this.replaceUrlFromState();
    this.loadWallpapers();
  }
  onSpaceAllToggle() {
    if (this.isSpaceAllSelected) {
      this.selectedSpaceIds = new Set();
      this.viewAllFilterType = null;
    } else {
      this.selectedSpaceIds = new Set();
      this.viewAllFilterType = 'space';
    }
    this.currentPage = 1;
    this.replaceUrlFromState();
    this.loadWallpapers();
  }

  // "All" state is driven by viewAllFilterType flag
  get isCategoryAllSelected(): boolean {
    return this.viewAllFilterType === 'category';
  }
  get isSpaceAllSelected(): boolean {
    return this.viewAllFilterType === 'space';
  }
  onThemeToggle(id: number) {
    const was = this.selectedThemeIds.has(id);
    this.selectedThemeIds = this.toggleIdSet(this.selectedThemeIds, id);
    this.pushFilterEvent('theme', String(id), was);
    this.currentPage = 1; this.replaceUrlFromState(); this.loadWallpapers();
  }
  onColorToggle(id: number) {
    const was = this.selectedColorIds.has(id);
    const name = (this.masters?.color || []).find((x: any) => x.id === id)?.name || String(id);
    this.selectedColorIds = this.toggleIdSet(this.selectedColorIds, id);
    this.pushFilterEvent('color', name, was);
    this.currentPage = 1; this.replaceUrlFromState(); this.loadWallpapers();
  }
  onCollectionToggle(id: number) {
    const was = this.selectedCollectionIds.has(id);
    this.selectedCollectionIds = this.toggleIdSet(this.selectedCollectionIds, id);
    this.pushFilterEvent('collection', String(id), was);
    this.currentPage = 1; this.loadWallpapers();
  }
  onSubCatToggle(id: number) {
    const was = this.selectedSubCatIds.has(id);
    this.selectedSubCatIds = this.toggleIdSet(this.selectedSubCatIds, id);
    this.pushFilterEvent('sub_category', String(id), was);
    this.currentPage = 1; this.loadWallpapers();
  }
  onTypeToggle(type: string) {
    const was = this.selectedTypes.has(type);
    this.selectedTypes = this.toggleStringSet(this.selectedTypes, type);
    this.pushFilterEvent('type', type, was);
    this.currentPage = 1; this.replaceUrlFromState(); this.loadWallpapers();
  }
  onQualityToggle(q: string) {
    const was = this.selectedQualities.has(q);
    this.selectedQualities = this.toggleStringSet(this.selectedQualities, q);
    this.pushFilterEvent('quality', q, was);
    this.currentPage = 1; this.replaceUrlFromState(); this.loadWallpapers();
  }

  onVastuToggle() {
    this.vastu = !this.vastu;
    this.pushFilterEvent('vastu', 'yes', !this.vastu);
    this.currentPage = 1; this.replaceUrlFromState(); this.loadWallpapers();
  }

  /** Get all active values for a given filter type, comma separated */
  private getActiveFilterValues(type: string): string {
    switch (type) {
      case 'category': return [...this.selectedCategoryIds].map(id => this.categoriesList.find(c => c.id === id)?.name || id).join(', ');
      case 'space': return [...this.selectedSpaceIds].map(id => this.spacesList.find(s => s.id === id)?.name || id).join(', ');
      case 'color': return [...this.selectedColorIds].map(id => (this.masters?.color || []).find((c: any) => c.id === id)?.name || id).join(', ');
      case 'theme': return [...this.selectedThemeIds].join(', ');
      case 'type': return [...this.selectedTypes].join(', ');
      case 'quality': return [...this.selectedQualities].join(', ');
      case 'vastu': return this.vastu ? 'yes' : '';
      default: return '';
    }
  }

  private pushFilterEvent(type: string, value: string, wasActive: boolean): void {
    // value = comma separated all active values of this type AFTER toggle
    const allValues = this.getActiveFilterValues(type);
    if (wasActive) {
      this.dataLayer.pushFilterRemove({
        filterType: type, filterValue: allValues || value,
        filtersActive: this.buildActiveFilters(),
        totalResults: this.totalRecords
      });
    } else {
      this.dataLayer.pushFilterApply({
        filterType: type, filterValue: allValues || value,
        totalActiveFilters: this.totalActiveFilterCount,
        totalResults: this.totalRecords
      });
    }
  }

  onSortChange(v: string) {
    const prev = this.sortBy;
    this.sortBy = v;
    this.dataLayer.pushSortChange({ sortType: v, previousSort: prev, totalResults: this.totalRecords });
    this.currentPage = 1; this.replaceUrlFromState(); this.loadWallpapers();
  }

  get masterSubCats(): MasterItem[] { return this.masters?.['sub-category'] || []; }

  getColorHex(id: number): string {
    return this.masterColors.find(c => c.id === id)?.hex || '#ccc';
  }
}
